import { File } from 'lucide-react'

import Text from '@/shared/components/common/text'
import { formatDateToDisplay } from '@/shared/lib/date-helpers'
import { EMPTY_LABEL } from '@/shared/constants/common-constants'
import { APPLICATION_SYNC_STATUS } from '@fingerprinting/types'
import ClientApplicationStatus from '@fingerprinting/features/client/application/status'
import { TClientApplicationDetail } from '@fingerprinting/types/schemas/application.schema'
import DataTableColumnHeader from '@/shared/components/common/data-table/plugins/column-header'
import { FIRST_APPLICATION_STATUS_OPTIONS } from '@fingerprinting/features/client/application/list/constants'

import SyncBadge from '../sync-badge'
import { APPLICATION_DATE } from '../overview/constants'
import { ActionsCell } from './components/actions-cell'

import { Checkbox } from '@/shared/components/ui/checkbox'

import type { IFiltersType } from './types'
import type { ColumnDef } from '@tanstack/react-table'

export const APPLICATION_TYPE_CODE = {
  ALL: 'all',
  PRIVACY_ACT: '10',
  PERMANENT_RESIDENT: '12',
  CANADIAN_CITIZENSHIP: '26',
  VISA_BORDER_CROSSING_FOREIGN_TRAVEL_WORK: '32',
  OTHERS: '39',
} as const

export const APPLICATION_TYPE_CODE_OPTIONS = [
  { value: APPLICATION_TYPE_CODE.ALL, label: 'All type' },
  {
    value: APPLICATION_TYPE_CODE.PRIVACY_ACT,
    label: 'CRIMINAL RECORD CHECK FOR PERSONAL USE (CMP-PPU-030)',
  },
  { value: APPLICATION_TYPE_CODE.PERMANENT_RESIDENT, label: 'PERMANENT RESIDENT STATUS' },
  { value: APPLICATION_TYPE_CODE.CANADIAN_CITIZENSHIP, label: 'CANADIAN CITIZENSHIP' },
  {
    value: APPLICATION_TYPE_CODE.VISA_BORDER_CROSSING_FOREIGN_TRAVEL_WORK,
    label: 'VISA/BORDER CROSSING/FOREIGN TRAVEL/WORK',
  },
  { value: APPLICATION_TYPE_CODE.OTHERS, label: 'Others' },
] as const satisfies Array<TSelectOption>

export const [FIRST_APPLICATION_TYPE_CODE_OPTIONS] = APPLICATION_TYPE_CODE_OPTIONS

export const DEFAULT_VALUE_FILTER = {
  typeCode: FIRST_APPLICATION_TYPE_CODE_OPTIONS.value,
  date: APPLICATION_DATE.alltime,
  status: FIRST_APPLICATION_STATUS_OPTIONS.value,
} as const satisfies IFiltersType

export const createColumns = (
  lastViewedMap: Record<string, string> = {}
): ColumnDef<TClientApplicationDetail>[] => {
  const checkIfNewAction = (row: TClientApplicationDetail) => {
    if ((row.unreadMessageCount ?? 0) > 0) return true
    const updatedAt = row.updatedAt || row.createdAt
    const lastViewed = lastViewedMap[row.id] || lastViewedMap[String(row.id)]
    if (!lastViewed) return true
    return new Date(updatedAt).getTime() > new Date(lastViewed).getTime()
  }

  return [
    {
      id: 'select-col',
      header: ({ table }) => (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ),
      size: 24,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title="ID" manualSorting />,
      cell: ({ row }) => {
        const displayId = row.original.fileNumber
          ? `#${row.original.fileNumber}`
          : `#${row.getValue('id')}`
        const isNew = checkIfNewAction(row.original)
        return (
          <div className="flex items-center gap-1.5 min-w-[165px]">
            <Text>{displayId}</Text>
            {isNew && <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" />}
          </div>
        )
      },
      enableSorting: true,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Application" manualSorting />
      ),
      cell: ({ row }) => (
        <Text className="max-w-lg truncate text-sm text-client-neutral-950">
          {row.getValue('name')}
        </Text>
      ),
      size: 200,
      enableSorting: true,
    },
    {
      accessorKey: 'typeName',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" manualSorting />,
      cell: ({ row }) => (
        <Text className="max-w-lg truncate text-sm text-client-neutral-950">
          {row.original.typeName ?? ''}
        </Text>
      ),
    },
    {
      accessorKey: 'reason',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Reason" manualSorting />,
      cell: ({ row }) => (
        <Text className="max-w-lg truncate text-sm text-client-neutral-950">
          {row.original.reason ?? EMPTY_LABEL}
        </Text>
      ),
    },
    {
      accessorKey: 'clientName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Client name" manualSorting />
      ),
      cell: ({ row }) => (
        <Text className="text-sm text-client-neutral-950">{row.getValue('clientName')}</Text>
      ),
      enableSorting: true,
    },
    {
      id: 'submittedDate',
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Submitted date" manualSorting />
      ),
      cell: ({ row }) => (
        <Text className="text-sm text-client-neutral-950">
          {formatDateToDisplay(row.original.createdAt, '')}
        </Text>
      ),
      size: 172,
      enableSorting: true,
    },
    {
      id: 'documents',
      header: 'Documents',
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-1">
            <File className="size-[18px] text-client-primary-900" />
            <Text className="text-sm text-client-neutral-950">
              {row.original.documentsUploaded ?? '--'}/{row.original.documentsRequired}
            </Text>
          </div>
        )
      },
      size: 145,
      enableSorting: false,
    },
    {
      accessorKey: 'syncStatus',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Synchronize" manualSorting />
      ),
      cell: ({ row }) => (
        <SyncBadge
          variant="ghost"
          status={row.getValue('syncStatus') ?? APPLICATION_SYNC_STATUS.NOT_SYNCED}
        />
      ),
      size: 140,
      enableSorting: true,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" manualSorting />,
      cell: ({ row }) => <ClientApplicationStatus variant="ghost" status={row.getValue('status')} />,
      size: 132,
      enableSorting: true,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => <ActionsCell row={row} />,
      size: 64,
      enableSorting: false,
      enableHiding: false,
    },
  ]
}
